import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  MenuItem,
  Popover,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import BaseButton from "../Common/Buttons/BaseButton";
import { commonStyles } from "../../../assets/MUIStyles/commonStyles";
import { leadDataStyles } from "../../../assets/MUIStyles/leadDataStyle";

const PatientTableFilters = ({ filtersObj }) => {
  const [clearFilter, setClearFilter] = useState(false);
  const {
    patientObj,
    popoverOpen,
    handlePopoverClose,
    anchorEl,
    columnOrder,
    insightsKeys,
  } = filtersObj;
  const { patientState, setPatinetState, fetchLeadsData } = patientObj;
  const { filters } = patientState;

  const allColumns = [
    ...columnOrder,
    ...insightsKeys.map((key) => ({
      key,
      label: key
        .replace(/([A-Z])/g, " $1")
        .replace(/^./, (str) => str.toUpperCase()),
    })),
  ]?.filter(({ key }) => key !== "insights");

  useEffect(() => {
    if (clearFilter) fetchLeadsData();
  }, [clearFilter]);

  const updateFilter = (index: number, e) => {
    const { name, value } = e.target;
    const newFilters = [...filters];
    newFilters[index] = { ...newFilters[index], [name]: value };
    setPatinetState((prevData: any) => ({
      ...prevData,
      filters: newFilters,
    }));
  };

  const addFilter = () => {
    const lastFilter = filters[0];

    if (!lastFilter.key || !lastFilter.condition || !lastFilter.value) {
      return;
    }
    setPatinetState((prevData: any) => ({
      ...prevData,
      filters: [{ key: "", condition: "contains", value: "" }, ...filters],
    }));
  };

  const removeFilter = (index: number) => {
    const updatedFilters = filters?.filter((_, i) => i !== index);
    setPatinetState((prevData: any) => ({
      ...prevData,
      filters: updatedFilters,
    }));
  };

  const applyFilters = async () => fetchLeadsData();
  const clearFilters = async () => {
    setPatinetState((prevData: any) => ({
      ...prevData,
      filters: [{ key: "", condition: "contains", value: "" }],
    }));
    setClearFilter(true);
  };

  return (
    <Popover
      open={popoverOpen}
      anchorEl={anchorEl}
      onClose={handlePopoverClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      slotProps={{
        paper: {
          style: { padding: 16, width: 655, borderRadius: 8 },
        },
      }}
    >
      <Box
        className="flex-column"
        sx={{
          gap: 1,
        }}
      >
        {filters?.map((filter, index) => (
          <Stack
            direction="row"
            spacing={1}
            key={index}
            sx={{
              alignItems: "center",
            }}
          >
            <Select
              name="key"
              value={filter.key}
              onChange={(e) => updateFilter(index, e)}
              displayEmpty
              size="small"
              sx={leadDataStyles.select}
            >
              <MenuItem value="">
                <em>Select Column</em>
              </MenuItem>
              {allColumns.map(({ key, label }, i) => (
                <MenuItem key={i} value={key as any}>
                  {label}
                </MenuItem>
              ))}
            </Select>

            <Select
              name="condition"
              value={filter.condition}
              onChange={(e) => updateFilter(index, e)}
              size="small"
              sx={leadDataStyles.select}
            >
              <MenuItem value="=">=</MenuItem>
              <MenuItem value="<">{"<"}</MenuItem>
              <MenuItem value=">">{">"}</MenuItem>
              <MenuItem value="contains">Contains</MenuItem>
            </Select>

            <TextField
              size="small"
              name="value"
              placeholder="Enter value"
              value={filter.value || ""}
              onChange={(e) => updateFilter(index, e)}
              sx={leadDataStyles.textField}
            />

            {index !== 0 && (
              <Button
                variant="contained"
                color="error"
                size="small"
                onClick={() => removeFilter(index)}
                sx={leadDataStyles.removeButton}
              >
                X
              </Button>
            )}

            {index === 0 && (
              <Button
                variant="outlined"
                onClick={addFilter}
                sx={commonStyles.clearButton}
                disabled={!filter?.key || !filter?.condition || !filter?.value}
              >
                + Add
              </Button>
            )}
          </Stack>
        ))}

        <Box
          className="flex-end"
          sx={{
            mt: 2,
          }}
        >
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              onClick={() => {
                clearFilters();
                handlePopoverClose();
              }}
              sx={commonStyles.clearButton}
            >
              Clear Filters
            </Button>

            <BaseButton
              title="Apply Filters"
              variant="contained"
              handleClick={() => {
                applyFilters();
                handlePopoverClose();
              }}
            />
          </Stack>
        </Box>
      </Box>
    </Popover>
  );
};

export default PatientTableFilters;
